/**
 * Start-of-page translation prefetch — the half of the handshake that lives in the app.
 *
 * `index.html` starts `fetch('/i18n/<lang>.json')` the moment the HTML is parsed and parks
 * the promise on `window.__i18nPrefetch`. Without it the root dictionary (~1.7 MB raw) is
 * requested by the APP_INITIALIZER, i.e. only after `main` and its ~400 chunks have been
 * downloaded, parsed and run — and bootstrap then waits for it before anything can render.
 * Fetched early, it arrives while the scripts are still downloading.
 *
 * The page script can only guess the language (stored choice, else the browser's), because
 * the signed-in preference is not known yet. A wrong guess costs one unused download and
 * nothing else: `takePrefetchedTranslation` hands the promise out only for the SAME language,
 * and only once, so a later language switch always asks the network for a fresh file.
 *
 * Keep the language-guess in `index.html` in step with `LocaleService.detect()` steps 2 and 5
 * (stored `user-language`, then `navigator.language`) and with `AVAILABLE_LANGS`.
 */
import type { Translation } from '@jsverse/transloco';

interface I18nPrefetch {
  lang: string;
  promise: Promise<Translation>;
}

declare global {
  interface Window {
    __i18nPrefetch?: I18nPrefetch;
  }
}

/** The prefetched dictionary for `lang`, or null when there is none (wrong language, already taken, failed). */
export function takePrefetchedTranslation(lang: string): Promise<Translation> | null {
  if (typeof window === 'undefined') return null;
  const pre = window.__i18nPrefetch;
  if (!pre || pre.lang !== lang) return null;
  window.__i18nPrefetch = undefined;
  return pre.promise;
}
